/*
  "A year with Miles" — scroll-driven seasonal paintings and beat states.

  Enhancement only. The markup already renders every beat readable and the
  backdrop already sits on its first painting, so if this file never runs the
  section is a legible list over a static driveway. Nothing here reveals
  content; it dims what you have not reached and crossfades decorative art.

  Each season holds for part of the scroll before the next painted frame enters.

  Not ported: the prototype ships a second block of code targeting
  `data-guard-card` / `data-guard-fill` / `data-guard-seq` attributes that exist
  in neither of its own HTML files. It is dead on arrival there and there is
  nothing here for it to drive.
*/
(function () {
  /* Shopify fingerprints an asset URL whenever its contents change. Using that
     URL as the revision means editor hot-reloads replace stale closures without
     relying on someone to remember a manual version bump. */
  var SCRIPT_REVISION = document.currentScript && document.currentScript.src
    ? document.currentScript.src
    : 'miles-year-timeline';

  /* A section can inject this asset again when Shopify's editor replaces it.
     Reuse a matching bootstrap, or retire an older revision before installing
     fresh document-level listeners. This deliberately diverges from the
     data-*-ready idiom the sibling miles-*.js sections share: their lifecycle
     has no teardown chain, and this file's WINDOW-level scroll/resize
     listeners would leak through every editor section:unload without one. */
  if (window.__milesYearTimeline) {
    if (window.__milesYearTimeline.revision === SCRIPT_REVISION) {
      window.__milesYearTimeline.init(document);
      return;
    }
    if (typeof window.__milesYearTimeline.teardown === 'function') {
      window.__milesYearTimeline.teardown();
    }
  }

  // Flat runs at each end and around spring hold a painting for a beat or two
  // rather than crossfading constantly.
  var STOPS = [
    [0.0, 'lateSummer'], [0.14, 'lateSummer'],
    [0.34, 'autumn'],
    [0.5, 'winter'],
    [0.6, 'spring'], [0.74, 'spring'],
    [0.88, 'summer'], [1.0, 'summer'],
  ];

  var SEASONS = ['lateSummer', 'autumn', 'winter', 'spring', 'summer'];

  function clamp(n, lo, hi) {
    return Math.min(hi, Math.max(lo, n));
  }

  function Timeline(section) {
    this.section = section;
    this.backdrop = section.querySelector('[data-year-backdrop]');
    this.fill = section.querySelector('[data-year-fill]');
    this.beats = Array.prototype.slice.call(section.querySelectorAll('[data-year-beat]'));
    this.seasonLayers = {};
    this.seasonStyles = {};

    Array.prototype.forEach.call(section.querySelectorAll('[data-year-season-layer]'), function (layer) {
      var season = layer.dataset.yearSeasonLayer;
      if (!this.seasonLayers[season]) this.seasonLayers[season] = [];
      this.seasonLayers[season].push(layer);
    }, this);

    if (!this.beats.length) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.ticking = false;
    this.frame = null;
    this.listening = false;
    this.destroyed = false;
    this.lastProgress = null;
    this.preloader = null;
    this.approachWarmed = false;
    this.resizeRebuild = null;

    var self = this;
    this.onScroll = function () {
      if (self.ticking) return;
      self.ticking = true;
      self.frame = window.requestAnimationFrame(function () {
        self.frame = null;
        if (self.destroyed || self.reduced.matches) {
          self.ticking = false;
          return;
        }
        self.update();
        self.ticking = false;
      });
    };
    this.onResize = function () {
      self.lastProgress = null;
      /* Rebuilding the approach observer re-anchors its rootMargin to the new
         viewport height, but native resize fires many times per second during
         a window drag — debounce so the drag settles into ONE rebuild instead
         of churning observers on every tick. */
      if (self.preloader && !self.approachWarmed) {
        if (self.resizeRebuild != null) window.clearTimeout(self.resizeRebuild);
        self.resizeRebuild = window.setTimeout(function () {
          self.resizeRebuild = null;
          if (self.destroyed || self.approachWarmed || !self.preloader) return;
          self.preloader.disconnect();
          self.preloader = null;
          self.watchApproach();
        }, 150);
      }
      self.onScroll();
    };
    this.onReducedChange = function (event) {
      if (event.matches) {
        self.stop();
        self.settle();
      } else {
        self.lastProgress = null;
        self.start();
        self.update();
      }
    };

    if (typeof this.reduced.addEventListener === 'function') {
      this.reduced.addEventListener('change', this.onReducedChange);
    } else if (typeof this.reduced.addListener === 'function') {
      this.reduced.addListener(this.onReducedChange);
    }

    if (this.reduced.matches) {
      this.settle();
    } else {
      this.start();
      this.update();
    }
  }

  Timeline.prototype.start = function () {
    if (this.destroyed || this.listening) return;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.listening = true;
    this.watchApproach();
  };

  Timeline.prototype.stop = function () {
    if (this.listening) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.listening = false;
    }
    if (this.frame != null) {
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    if (this.preloader) {
      this.preloader.disconnect();
      this.preloader = null;
    }
    if (this.resizeRebuild != null) {
      window.clearTimeout(this.resizeRebuild);
      this.resizeRebuild = null;
    }
    this.ticking = false;
  };

  Timeline.prototype.destroy = function () {
    if (this.destroyed) return;
    this.stop();
    if (this.reduced && typeof this.reduced.removeEventListener === 'function') {
      this.reduced.removeEventListener('change', this.onReducedChange);
    } else if (this.reduced && typeof this.reduced.removeListener === 'function') {
      this.reduced.removeListener(this.onReducedChange);
    }
    this.destroyed = true;
  };

  /* The opening frame is a native lazy image. Only when the timeline comes
     within one viewport do we warm its first transition; later paintings are
     hydrated incrementally as scroll progress approaches them. This avoids
     five overlapping lazy images all entering the browser's threshold at once. */
  Timeline.prototype.watchApproach = function () {
    if (this.approachWarmed || this.preloader || this.reduced.matches) return;

    var self = this;
    var warmOpening = function () {
      self.promoteOpening();
      self.hydrateSeason('lateSummer');
      self.hydrateSeason('autumn');
      self.approachWarmed = true;
      if (self.preloader) {
        self.preloader.disconnect();
        self.preloader = null;
      }
    };

    if (!('IntersectionObserver' in window)) {
      warmOpening();
      return;
    }

    this.preloader = new IntersectionObserver(function (entries) {
      if (entries.some(function (entry) { return entry.isIntersecting; })) warmOpening();
    }, { rootMargin: window.innerHeight + 'px 0px' });
    this.preloader.observe(this.section);
  };

  Timeline.prototype.promoteOpening = function () {
    (this.seasonLayers.lateSummer || []).forEach(function (layer) {
      var image = layer.querySelector('img');
      if (image) image.loading = 'eager';
    });
  };

  Timeline.prototype.hydrateSeason = function (season) {
    (this.seasonLayers[season] || []).forEach(function (layer) {
      if (layer.dataset.yearHydrated === 'true') return;

      Array.prototype.forEach.call(layer.querySelectorAll('source[data-year-srcset]'), function (source) {
        source.srcset = source.dataset.yearSrcset;
        delete source.dataset.yearSrcset;
      });

      var image = layer.querySelector('img');
      if (image) {
        /* A deferred seasonal image is now a deliberate preload, not generic
           lazy content. Low fetch priority keeps it behind critical assets. */
        var deferred = !!(image.dataset.yearSrc || image.dataset.yearSrcset);
        if (deferred) {
          image.loading = 'eager';
          /* An instant scroll (End key, anchor jump) reaches paint() before
             the network does. Hide BEFORE assigning the deferred sources —
             a cached result can settle synchronously, and image.complete is
             true for broken images too. Load (always delivered async, cached
             or not) reveals with a fade; error leaves it hidden so the
             broken-image glyph never paints over the late-summer safety
             layer beneath. */
          image.classList.add('is-arriving');
          var onSettled = function (event) {
            image.removeEventListener('load', onSettled);
            image.removeEventListener('error', onSettled);
            if (event.type === 'load') image.classList.remove('is-arriving');
          };
          image.addEventListener('load', onSettled);
          image.addEventListener('error', onSettled);
        }
        if (image.dataset.yearSrcset) {
          image.srcset = image.dataset.yearSrcset;
          delete image.dataset.yearSrcset;
        }
        if (image.dataset.yearSrc) {
          image.src = image.dataset.yearSrc;
          delete image.dataset.yearSrc;
        }
      }

      layer.dataset.yearHydrated = 'true';
    });
  };

  /* Every beat active, backdrop on its default painting, progress bar full. The
     state someone gets who has asked not to see motion — the same information,
     none of the movement. */
  Timeline.prototype.settle = function () {
    this.beats.forEach(function (beat) {
      beat.classList.remove('is-upcoming', 'is-past');
      beat.classList.add('is-active');
    });
    if (this.fill) this.fill.style.height = '100%';
    this.paint(0, true);
  };

  Timeline.prototype.paint = function (progress, staticOnly) {
    if (!this.backdrop) return;

    var i = 0;
    while (i < STOPS.length - 2 && progress > STOPS[i + 1][0]) i++;

    var from = STOPS[i];
    var to = STOPS[i + 1];
    var span = to[0] - from[0];
    var t = span === 0 ? 0 : clamp((progress - from[0]) / span, 0, 1);

    this.hydrateSeason(from[1]);
    this.hydrateSeason(to[1]);

    /* During a hold, or once a transition is underway, warm the next distinct
       season early enough that its first painted pixel never arrives late. */
    if (!staticOnly && (from[1] === to[1] || t > 0.25)) {
      for (var next = i + 2; next < STOPS.length; next++) {
        if (STOPS[next][1] !== to[1]) {
          this.hydrateSeason(STOPS[next][1]);
          break;
        }
      }
    }

    var styles = {};
    SEASONS.forEach(function (season) {
      styles[season] = { opacity: 0, z: 0 };
    });
    /* Late summer is the always-loaded safety painting. It stays underneath
       every other season so a fast jump, slow request or image error can never
       expose an empty backdrop. */
    styles.lateSummer = { opacity: 1, z: 0 };

    if (from[1] === to[1]) {
      styles[from[1]] = { opacity: 1, z: 1 };
    } else {
      /* Keep the outgoing painting opaque and lay the incoming one over it.
         Two half-transparent layers only cover 75% of the backdrop at their
         midpoint, which creates a visible brightness pulse. Dynamic stacking
         also makes this compositing correct when someone scrolls upward. */
      styles[from[1]] = { opacity: 1, z: 1 };
      styles[to[1]] = { opacity: t, z: 2 };
    }

    SEASONS.forEach(function (season) {
      var opacity = styles[season].opacity.toFixed(3);
      var z = String(styles[season].z);
      var signature = opacity + ':' + z;
      if (this.seasonStyles[season] !== signature) {
        (this.seasonLayers[season] || []).forEach(function (layer) {
          layer.style.setProperty('--season-opacity', opacity);
          layer.style.setProperty('--season-z', z);
        });
        this.seasonStyles[season] = signature;
      }
    }, this);
  };

  Timeline.prototype.update = function () {
    var rect = this.section.getBoundingClientRect();
    var vh = window.innerHeight;

    // Nothing in this section is visible. The CSS/no-JS opening frame is
    // already correct above it, and the last visible update reaches the summer
    // endpoint before it leaves below, so detached work here buys nothing.
    if (rect.top >= vh || rect.bottom <= 0) return;

    // How far the section has travelled through the viewport, 0 to 1. A
    // section at most one viewport tall has no interior travel (the
    // denominator hits 0 or goes negative), so it walks enter-to-exit
    // progress instead — pinning it to 1 skipped every season but the last
    // for its entire time on screen.
    var travel = rect.height - vh;
    var progress = travel > 0
      ? clamp((0 - rect.top) / travel, 0, 1)
      : clamp((vh - rect.top) / (vh + rect.height), 0, 1);

    /* Finish every layout read before paint(), class changes or inline styles
       invalidate geometry for this frame. */
    var beatRects = this.beats.map(function (beat) {
      return beat.getBoundingClientRect();
    });

    // Above and below the section progress sits at exactly 0 or 1. Do not
    // rewrite every decorative transform while someone scrolls the rest of the
    // page; resize explicitly invalidates this cache.
    if (progress !== this.lastProgress) {
      this.paint(progress);
      this.lastProgress = progress;
    }

    var lowestActiveIndex = -1;

    this.beats.forEach(function (beat, index) {
      var b = beatRects[index];
      beat.classList.remove('is-upcoming', 'is-past', 'is-active');

      if (b.top > vh * 0.8) {
        beat.classList.add('is-upcoming');
      } else if (b.bottom < vh * 0.25) {
        beat.classList.add('is-past');
        lowestActiveIndex = index;
      } else {
        beat.classList.add('is-active');
        lowestActiveIndex = index;
      }
    });

    if (this.fill) {
      if (lowestActiveIndex < 0) {
        this.fill.style.height = '0px';
      } else {
        var track = beatRects[0].top;
        var reached = beatRects[lowestActiveIndex].top - track;
        this.fill.style.height = Math.max(0, reached) + 'px';
      }
    }
  };

  function sectionsWithin(root) {
    var sections = [];
    if (root && typeof root.matches === 'function' && root.matches('[data-year-section]')) {
      sections.push(root);
    }
    if (root && typeof root.querySelectorAll === 'function') {
      sections = sections.concat(Array.prototype.slice.call(root.querySelectorAll('[data-year-section]')));
    }
    return sections;
  }

  function init(root) {
    sectionsWithin(root || document).forEach(function (section) {
      if (section._milesYearTimelineRevision === SCRIPT_REVISION && section._milesYearTimeline) return;
      if (section._milesYearTimeline && typeof section._milesYearTimeline.destroy === 'function') {
        section._milesYearTimeline.destroy();
      }
      section._milesYearTimeline = new Timeline(section);
      section._milesYearTimelineRevision = SCRIPT_REVISION;
      section.dataset.yearReady = 'true';
    });
  }

  function destroy(root) {
    sectionsWithin(root).forEach(function (section) {
      if (section._milesYearTimeline) section._milesYearTimeline.destroy();
      delete section._milesYearTimeline;
      delete section._milesYearTimelineRevision;
      delete section.dataset.yearReady;
    });
  }

  function onSectionLoad(event) {
    init(event.target);
  }

  function onSectionUnload(event) {
    destroy(event.target);
  }

  function teardown() {
    destroy(document);
    document.removeEventListener('shopify:section:load', onSectionLoad);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
  }

  window.__milesYearTimeline = {
    revision: SCRIPT_REVISION,
    init: init,
    destroy: destroy,
    teardown: teardown,
  };

  init(document);
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
})();
